import styles from './PaymentsComparisonModal.module.scss';

import React, { useCallback, useEffect, useRef, useState } from 'react';

import type { PaymentsComparisonModalV2 as PaymentsComparisonModalType } from 'api/contentful/contentTypes.cloverdotcom';
import { StructuredModal, StructuredModalState } from 'components/StructuredModal/StructuredModal';
import { TextV2 } from 'components/TextV2/TextV2';
import { useBreakpoints } from 'hooks/useBreakpoints';
import type { FlatEntry } from 'services/cms/flattenContentful';
import { PaymentsComparisonModalBody } from './PaymentsComparisonModalBody/PaymentsComparisonModalBody';
import { PaymentsComparisonModalHeader } from './PaymentsComparisonModalHeader/PaymentsComparisonModalHeader';
import { PaymentsComparisonDisclaimer } from './PaymentsComparisonDisclaimer/PaymentsComparisonDisclaimer';
import cx from 'classnames';

export enum PaymentOptions {
  MONTHLY,
  UPFRONT,
}

interface IPaymentsComparisonModalProps {
  modalState: StructuredModalState;
  entry: FlatEntry<PaymentsComparisonModalType>;
}

export const PaymentsComparisonModal: React.FC<IPaymentsComparisonModalProps> = ({ modalState, entry }) => {
  const { browser } = useBreakpoints();
  const isDesktopOrLarge = browser.is.desktop;
  const [activePaymentOption, setActivePaymentOption] = useState<PaymentOptions>(PaymentOptions.MONTHLY);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const observeBottomTrigger = useCallback(
    node => {
      if (node !== null) {
        observerRef.current = new IntersectionObserver(
          ([intersectionEntry]) => {
            if (intersectionEntry.isIntersecting) {
              setHasScrolledToBottom(true);
            } else {
              setHasScrolledToBottom(false);
            }
          },
          {
            root: containerRef.current,
            threshold: 1,
          },
        );

        observerRef.current.observe(node);
      }
    },
    [containerRef],
  );

  useEffect(() => {
    return () => {
      if (observerRef.current && typeof observerRef.current.disconnect === 'function') {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const {
    title,
    sideHeadingOptionItems,
    monthlyOptionHeading,
    upfrontOptionHeading,
    monthlyOptionItems,
    upfrontOptionItems,
    disclaimerItems,
  } = entry;

  if (
    !monthlyOptionHeading?.length ||
    !upfrontOptionHeading?.length ||
    !monthlyOptionItems?.length ||
    !upfrontOptionItems?.length ||
    !sideHeadingOptionItems?.length ||
    !disclaimerItems?.length
  )
    return null;

  return (
    <StructuredModal modalState={modalState} aria={{ modal: true, labelledby: 'PaymentsComparisonModal' }}>
      <div
        data-testid="payments-comparison-modal"
        className={cx(styles.container, { [styles.scrolledToBottom]: hasScrolledToBottom })}
        ref={containerRef}
      >
        <TextV2 id={title} value={title} variant="h3-medium" tagName="h3" className={styles.containerTitle} />
        <PaymentsComparisonModalHeader
          activePaymentOption={activePaymentOption}
          setActivePaymentOption={setActivePaymentOption}
          monthlyOptionHeading={monthlyOptionHeading}
          upfrontOptionHeading={upfrontOptionHeading}
          isTabs={!isDesktopOrLarge}
        />
        <PaymentsComparisonModalBody
          activePaymentOption={activePaymentOption}
          sideHeadingOptionItems={sideHeadingOptionItems}
          monthlyOptionItems={monthlyOptionItems}
          upfrontOptionItems={upfrontOptionItems}
          isFullWidth={!isDesktopOrLarge}
        />
        <PaymentsComparisonDisclaimer disclaimerItems={disclaimerItems} />
        <div className={styles.bottomTrigger} ref={observeBottomTrigger} />
      </div>
    </StructuredModal>
  );
};

PaymentsComparisonModal.displayName = 'PaymentsComparisonModal';
